import {ChangeDetectionStrategy, Component, Inject, Injectable} from "@angular/core";
import {ArkivnodeId, NyMappeSpesifikasjon, OppdaterMappeSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MatButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {ReactiveFormsModule} from "@angular/forms";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {ArkivmappeSkjemaKomponent} from "web-ng2/moduler/arkiv/komponenter/arkivmappe-skjema/arkivmappe-skjema";
import {ArkivDatatjeneste, ArkivMappenodeDLM} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";

const debug = initDebugger('ArkivOpprettRedigerMappKomponent');

/**
 * Dialog for opprettelse og redigering av mappe
 */
@Component({
    selector: 'arkivmappe-opprett-rediger-dialog',
    templateUrl: 'arkivmappe-opprett-rediger-dialog.html',
    styleUrl: 'arkivmappe-opprett-rediger-dialog.scss',
    imports: [
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        TranslateModule,
        ReactiveFormsModule,
        SubmitButtonKomponent,
        ArkivmappeSkjemaKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArkivmappeOpprettRedigerDialogKomponent {

    static visOpprettMappe(dialog: MatDialog, forelderMappe: ArkivMappenodeDLM) {
        return dialog.open<ArkivmappeOpprettRedigerDialogKomponent, DialogParametre, void>(ArkivmappeOpprettRedigerDialogKomponent,
          {
              data: {mappe: forelderMappe, erOpprett: true},
              width: `${150 + RiktekstRedigeringKomponent.mat_form_field_riktekst_redigering_min_bredde_px}px`
          });
    }

    static visRedigerMappe(dialog: MatDialog, mappe: ArkivMappenodeDLM) {
        return dialog.open<ArkivmappeOpprettRedigerDialogKomponent, DialogParametre, void>(ArkivmappeOpprettRedigerDialogKomponent,
          {
              data: {mappe: mappe, erOpprett: false},
              width: `${150 + RiktekstRedigeringKomponent.mat_form_field_riktekst_redigering_min_bredde_px}px`
          });
    }

    /** Mappen som skal redigeres (dersom erOpprett), eller forelder-mappen som ny mappe skal legges til i (dersom !erOpprett) */
    mappe: ArkivMappenodeDLM; //fra DialogParametre
    erOpprett: boolean; //fra DialogParametre

    skjema = ArkivmappeSkjemaKomponent.opprettSkjema();
    lagrer = asynkArbeidPågårSignal();

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre, public dialogRef: MatDialogRef<ArkivmappeOpprettRedigerDialogKomponent>, private avhengigheter: ArkivmappeOpprettRedigerDialogKomponentAvhengigheter) {
        this.mappe = parametre.mappe;
        this.erOpprett = parametre.erOpprett;

        if (!this.erOpprett) ArkivmappeSkjemaKomponent.populerSkjema(this.skjema, this.mappe);
    }

    async lagre() {
        debug('lagrer')
        const spesifikasjon = ArkivmappeSkjemaKomponent.somSpesifikasjon(this.skjema);
        if (this.erOpprett)
            await this.lagrer.utfør(() => this.avhengigheter.opprett(this.mappe.id, spesifikasjon));
        else
            await this.lagrer.utfør(() => this.avhengigheter.oppdater(this.mappe.id, spesifikasjon));

        this.dialogRef.close(true);
    }

    avbryt() {
        this.dialogRef.close(false);
    }
}

export interface DialogParametre {
    mappe: ArkivMappenodeDLM;
    erOpprett: boolean;
}

@Injectable()
export class ArkivmappeOpprettRedigerDialogKomponentAvhengigheter {

    constructor(private arkivDatatjeneste: ArkivDatatjeneste) {
    }

    async opprett(forelderId: ArkivnodeId, spesifikasjon: NyMappeSpesifikasjon): Promise<void> {
        await this.arkivDatatjeneste.opprettMappe(forelderId, spesifikasjon);
    }

    async oppdater(mappeId: ArkivnodeId, spesifikasjon: OppdaterMappeSpesifikasjon): Promise<void> {
        await this.arkivDatatjeneste.oppdaterMappe(mappeId, spesifikasjon);
    }
}
